/**
 * Intercars Integration
 * Fetches dynamic variant data from the backend and handles dynamic variant creation on Add to Cart.
 */
(function () {
  const INTERCARS_STRINGS = {
    loadingTitle: "Lisätään tuotetta ostoskoriin",
    loadingText: "Odota hetki, älä sulje sivua.",
    errorTitle: "Tuotteen lisääminen epäonnistui",
    errorText: "Päivitä sivu ja yritä uudelleen.",
    noLocationTitle: "Valitse ensin toimitusaika.",
    noVariantTitle: "Tälle toimitusajalle ei voitu luoda tuotevaihtoehtoa.",
    noVariantText: "Valitse toinen toimitusaika tai päivitä sivu.",
    reloadButton: "Päivitä sivu",
    closeButton: "Sulje",
    notEnoughStockTitle: "Varastossa ei ole riittävästi tuotteita.",
    notEnoughStockText: (available, inCart) =>
      inCart > 0
        ? `Voit lisätä vielä ${available} kpl (ostoskorissa on jo ${inCart} kpl).`
        : `Saatavilla yhteensä ${available} kpl.`,
    inStock: (available, inCart) =>
      inCart > 0
        ? `Varastossa ${available} kpl, ostoskorissa ${inCart} kpl`
        : `Varastossa ${available} kpl`,
    splitNoteTitle: "Tilaus toimitetaan osissa:",
    otherLocationNoteTitle: "Valitun toimitusajan tuotteet ovat jo ostoskorissa. Tilaus toimitetaan:",
    splitNoteLine: (quantity, deliveryText) => `${quantity} kpl – ${deliveryText}`,
    drawerSplitTitle: "Loput lisättiin toisella toimitusajalla:",
  };

  /**
   * Splits the requested quantity across Intercars locations: the selected
   * location is used first, then the other in-stock locations from the fastest
   * delivery to the slowest (ties keep the backend order). A customer ordering
   * 15 when the selected location has 10 gets 10 from it and 5 from the next one.
   *
   * @returns {{ allocations: Array<{ location: object, quantity: number }>, remaining: number }}
   *   `remaining` > 0 means the combined stock is not enough.
   */
  function allocateIntercarsQuantity(locations, selectedLocationId, quantity) {
    const list = Array.isArray(locations) ? locations : [];
    const selected = list.find(
      (loc) => String(loc.shopifyLocationId) === String(selectedLocationId),
    );
    const deliveryDays = (loc) => {
      const days = Number(loc.deliveryDays);
      return Number.isFinite(days) ? days : Infinity;
    };
    const others = list
      .filter((loc) => loc !== selected)
      .map((loc, index) => ({ loc, index }))
      .sort((a, b) => deliveryDays(a.loc) - deliveryDays(b.loc) || a.index - b.index)
      .map(({ loc }) => loc);
    const ordered = (selected ? [selected] : []).concat(others);

    let remaining = Math.max(0, parseInt(quantity, 10) || 0);
    const allocations = [];
    for (const location of ordered) {
      if (remaining <= 0) break;
      const available = Math.max(0, Number(location.available) || 0);
      const take = Math.min(available, remaining);
      if (take > 0) {
        allocations.push({ location, quantity: take });
        remaining -= take;
      }
    }
    return { allocations, remaining };
  }

  function getTotalIntercarsStock(locations) {
    return (Array.isArray(locations) ? locations : []).reduce(
      (sum, loc) => sum + Math.max(0, Number(loc.available) || 0),
      0,
    );
  }

  /**
   * Customer-facing name of locations that are not worded as a delivery time.
   * Intercars locations are named from the API's `deliveryDays`, which is also
   * what their variant titles ("2 päivän kuluessa") follow.
   */
  const LOCATION_LABELS = {
    "2940502047": "Herttoniemessä", // Herttoniemi
  };

  /** Locations not offered to customers at all — not listed, never allocated. */
  const HIDDEN_LOCATIONS = new Set([
    "113754079576", // Intercars REN (V99)
  ]);

  function locationKey(location) {
    return String(location.shopifyLocationId || "").split("/").pop();
  }

  function formatDeliveryText(location) {
    return (
      LOCATION_LABELS[locationKey(location)] ||
      `${Number(location.deliveryDays) === 2 ? "1-2" : location.deliveryDays} ${Number(location.deliveryDays) === 1 ? "päivä" : "päivää"}`
    );
  }

  window.IntercarsIntegration = {
    allocateQuantity: allocateIntercarsQuantity,
    getTotalStock: getTotalIntercarsStock,
    formatDeliveryText,
    isHiddenLocation: (location) => HIDDEN_LOCATIONS.has(locationKey(location)),
  };

  /**
   * Full-page blocking overlay shown while an Intercars variant is created and
   * pushed to the cart. That flow (update-or-create-variant + retrying
   * cart/add.js until the fresh variant is queryable) can take tens of seconds,
   * so the button spinner alone is not enough feedback. Also renders the error
   * state, replacing the previous alert() calls.
   */
  const IntercarsOverlay = (function () {
    const STYLE_ID = "intercars-overlay-styles";
    const SPINNER_HTML =
      '<div class="intercars-overlay__spinner" aria-hidden="true"></div>';
    const ERROR_ICON_HTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" width="44" height="44">
        <circle cx="12" cy="12" r="10" stroke="#ef4444" stroke-width="2"></circle>
        <path d="M12 7v6" stroke="#ef4444" stroke-width="2" stroke-linecap="round"></path>
        <circle cx="12" cy="16.5" r="1.15" fill="#ef4444"></circle>
      </svg>
    `;

    let root = null;
    let iconEl = null;
    let titleEl = null;
    let textEl = null;
    let actionsEl = null;
    let mode = "hidden"; // "hidden" | "loading" | "error"
    let previousBodyOverflow = "";
    let previouslyFocused = null;

    function injectStyles() {
      if (document.getElementById(STYLE_ID)) return;
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.textContent = `
        .intercars-overlay {
          position: fixed;
          inset: 0;
          z-index: 2147483000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(0, 0, 0, 0.55);
        }
        .intercars-overlay[hidden] { display: none; }
        /* Quick view already dims the page with its own .m-modal backdrop —
           stacking a second one on top turns everything near-black. */
        body:has(.m-modal.m-open-modal) .intercars-overlay {
          background: transparent;
        }
        .intercars-overlay__box {
          width: 100%;
          max-width: 380px;
          padding: 28px 24px;
          border-radius: 12px;
          text-align: center;
          /* Theme color vars hold bare RGB triples ("255,255,255"), not colors,
             so they must be wrapped in rgb() — see snippets/style-tags.liquid. */
          background-color: rgb(var(--color-background, 255, 255, 255));
          color: rgb(var(--color-foreground, 26, 26, 26));
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
        }
        .intercars-overlay__icon {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 44px;
          margin-bottom: 16px;
        }
        .intercars-overlay__spinner {
          width: 40px;
          height: 40px;
          border: 3px solid currentColor;
          border-top-color: transparent;
          border-radius: 50%;
          opacity: 0.85;
          animation: intercars-overlay-spin 0.8s linear infinite;
        }
        @keyframes intercars-overlay-spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) {
          .intercars-overlay__spinner { animation-duration: 2.4s; }
        }
        .intercars-overlay__title {
          margin: 0 0 8px;
          font-size: 17px;
          font-weight: 600;
          line-height: 1.35;
        }
        .intercars-overlay__text {
          margin: 0;
          font-size: 14px;
          line-height: 1.5;
          opacity: 0.75;
        }
        .intercars-overlay__text[hidden] { display: none; }
        .intercars-overlay__actions {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: center;
          margin-top: 20px;
        }
        .intercars-overlay__actions[hidden] { display: none; }
        .intercars-overlay__btn {
          padding: 10px 18px;
          border: 1px solid currentColor;
          border-radius: 6px;
          background: transparent;
          color: inherit;
          font: inherit;
          font-size: 14px;
          line-height: 1.2;
          cursor: pointer;
        }
        .intercars-overlay__btn--primary {
          background-color: rgb(var(--color-foreground, 26, 26, 26));
          color: rgb(var(--color-background, 255, 255, 255));
        }
      `;
      document.head.appendChild(style);
    }

    function build() {
      if (root) return;
      injectStyles();

      root = document.createElement("div");
      root.className = "intercars-overlay";
      root.setAttribute("role", "alertdialog");
      root.setAttribute("aria-modal", "true");
      root.setAttribute("aria-live", "assertive");
      root.hidden = true;
      root.innerHTML = `
        <div class="intercars-overlay__box">
          <div class="intercars-overlay__icon"></div>
          <p class="intercars-overlay__title"></p>
          <p class="intercars-overlay__text"></p>
          <div class="intercars-overlay__actions" hidden></div>
        </div>
      `;

      iconEl = root.querySelector(".intercars-overlay__icon");
      titleEl = root.querySelector(".intercars-overlay__title");
      textEl = root.querySelector(".intercars-overlay__text");
      actionsEl = root.querySelector(".intercars-overlay__actions");

      // Only the error state is dismissible — while loading we keep the overlay
      // locked so the customer can't fire a second add-to-cart mid-flight.
      root.addEventListener("click", (e) => {
        if (mode === "error" && e.target === root) hide();
      });
      document.addEventListener("keydown", (e) => {
        if (mode === "error" && e.key === "Escape") hide();
      });

      document.body.appendChild(root);
    }

    function open() {
      build();
      if (mode === "hidden") {
        previouslyFocused = document.activeElement;
        previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
      }
      root.hidden = false;
    }

    function show() {
      open();
      mode = "loading";
      iconEl.innerHTML = SPINNER_HTML;
      titleEl.textContent = INTERCARS_STRINGS.loadingTitle;
      textEl.textContent = INTERCARS_STRINGS.loadingText;
      textEl.hidden = false;
      actionsEl.innerHTML = "";
      actionsEl.hidden = true;
    }

    function error(options) {
      const opts = options || {};
      open();
      mode = "error";
      iconEl.innerHTML = ERROR_ICON_HTML;
      titleEl.textContent = opts.title || INTERCARS_STRINGS.errorTitle;
      textEl.textContent = opts.text || "";
      textEl.hidden = !opts.text;
      actionsEl.innerHTML = "";
      actionsEl.hidden = false;

      if (opts.reload !== false) {
        const reloadBtn = document.createElement("button");
        reloadBtn.type = "button";
        reloadBtn.className =
          "intercars-overlay__btn intercars-overlay__btn--primary";
        reloadBtn.textContent = INTERCARS_STRINGS.reloadButton;
        reloadBtn.addEventListener("click", () => window.location.reload());
        actionsEl.appendChild(reloadBtn);
      }

      const closeBtn = document.createElement("button");
      closeBtn.type = "button";
      closeBtn.className = "intercars-overlay__btn";
      closeBtn.textContent = INTERCARS_STRINGS.closeButton;
      closeBtn.addEventListener("click", hide);
      actionsEl.appendChild(closeBtn);

      const firstButton = actionsEl.firstElementChild;
      if (firstButton) firstButton.focus();
    }

    function hide() {
      if (!root || mode === "hidden") return;
      const wasError = mode === "error";
      mode = "hidden";
      root.hidden = true;
      document.body.style.overflow = previousBodyOverflow;
      // Restore focus only on a user-dismissed error — on success the cart
      // drawer opens right after and manages focus itself.
      if (
        wasError &&
        previouslyFocused &&
        typeof previouslyFocused.focus === "function"
      ) {
        previouslyFocused.focus();
      }
      previouslyFocused = null;
    }

    return { show, error, hide };
  })();

  /**
   * A variant the Admin API has just created is not immediately usable by
   * cart/add.js, and it settles in two stages: the variant becomes visible, then
   * its inventory catches up. Until both have happened we keep driving the
   * submit ourselves (with retries) rather than handing off to the theme's
   * single-shot native handler, which would surface a raw error.
   *
   * Generous on purpose — being early here costs a customer a failed add, while
   * being late only costs a brief overlay. The console logs the attempt a fresh
   * variant first succeeded on; tune this once that number is known.
   */
  const VARIANT_SETTLE_MS = 30000;

  /**
   * Variant creation is deduplicated per product+location across every form on
   * the page — PDP and quick view can render the same product at once. Callers
   * get the in-flight or already-resolved promise, so pre-warming on render and
   * a later Add to cart click never cause two Admin API writes.
   */
  const variantCreation = new Map(); // key -> { promise, variantId, settledAt }

  function variantCacheKey(productGid, locationId) {
    return `${productGid}|${locationId}`;
  }

  function createIntercarsVariant(apiBaseUrl, productGid, locationId) {
    const key = variantCacheKey(productGid, locationId);
    const cached = variantCreation.get(key);
    if (cached) return cached.promise;

    const entry = { promise: null, variantId: null, settledAt: 0 };

    entry.promise = fetch(
      `${apiBaseUrl}/api/intercars/update-or-create-variant`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          shopifyProductId: productGid,
          shopifyLocationId: locationId,
        }),
      },
    )
      .then((res) => res.json())
      .then((data) => {
        if (!data || !data.variantId) {
          const err = new Error(
            "update-or-create-variant returned no variantId",
          );
          err.intercarsNoVariant = true;
          throw err;
        }
        entry.variantId = String(data.variantId).split("/").pop();
        entry.settledAt = Date.now();
        return entry.variantId;
      })
      .catch((err) => {
        // Never cache a failure — a later click must be able to try again.
        variantCreation.delete(key);
        throw err;
      });

    variantCreation.set(key, entry);
    return entry.promise;
  }

  function getSettledVariantId(productGid, locationId) {
    const entry = variantCreation.get(variantCacheKey(productGid, locationId));
    if (!entry || !entry.variantId) return null;
    if (Date.now() - entry.settledAt < VARIANT_SETTLE_MS) return null;
    return entry.variantId;
  }

  /** Numeric variant id for a location, if it exists or was created on this page. */
  function knownVariantId(productGid, location) {
    const entry = variantCreation.get(
      variantCacheKey(productGid, location.shopifyLocationId),
    );
    const id = location.shopifyProductVariantId || (entry && entry.variantId);
    return id ? String(id).split("/").pop() : null;
  }

  // Newly created variants can take a few seconds to become available to
  // cart/add.js (eventual consistency after update-or-create-variant), so
  // we retry with a growing delay — but only for errors that actually
  // mean "not there yet".
  const ADD_TO_CART_MAX_ATTEMPTS = 7; // initial attempt + 6 retries
  const ADD_TO_CART_RETRY_DELAY_MS = 1500;
  const ADD_TO_CART_RETRY_DELAY_MAX_MS = 6000;

  /**
   * Propagation to the storefront happens in two stages: first the variant
   * itself is missing (404 "cannot find variant"), then it exists but its
   * inventory is still zero (422 "on jo loppuunmyyty"). Both are "not there
   * yet" and must be waited out.
   *
   * A 422 is also how a genuinely sold-out variant answers. Allocations only
   * take quantities Intercars reports as free, so for a variant created on
   * this click a sold-out answer is inventory lag. For variants that already
   * existed it is the truth — don't wait it out.
   *
   * 429 and 5xx are transient. Retrying a 429 without honouring Retry-After
   * only makes the rate limit worse.
   */
  function isRetryableCartStatus(status, hasFreshVariant) {
    if (status === 404 || status === 429 || status >= 500) return true;
    if (status === 422) return hasFreshVariant;
    return false;
  }

  function computeRetryDelay(attempt, res) {
    const retryAfter = parseInt(res.headers.get("Retry-After") || "", 10);
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      return retryAfter * 1000;
    }
    const base = Math.min(
      ADD_TO_CART_RETRY_DELAY_MS * (attempt + 1),
      ADD_TO_CART_RETRY_DELAY_MAX_MS,
    );
    // Jitter keeps simultaneous shoppers from retrying in lockstep.
    return Math.round(base * (1 + Math.random() * 0.3));
  }

  /**
   * POSTs to cart/add.js, retrying while a fresh variant settles.
   * `buildRequest` is called per attempt so FormData bodies are never reused.
   * Resolves with the response JSON; rejects with an Error carrying
   * `cartStatus`, `cartError` (Shopify's JSON) and `retryable` when Shopify
   * refused the add, or with the network error as-is.
   */
  function addToCartWithRetry(buildRequest, hasFreshVariant) {
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    function attemptAdd(attempt) {
      const request = buildRequest();
      return fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: request.headers,
        body: request.body,
      }).then(async (res) => {
        if (res.ok) {
          if (attempt > 0) {
            // The propagation delay for a freshly created variant, in
            // attempts. Use it to tune VARIANT_SETTLE_MS and the window.
            console.info(
              `[Intercars Integration] Add to cart succeeded on attempt ${attempt + 1}.`,
            );
          }
          return res.json();
        }

        const retryable = isRetryableCartStatus(res.status, hasFreshVariant);
        if (retryable && attempt < ADD_TO_CART_MAX_ATTEMPTS - 1) {
          await wait(computeRetryDelay(attempt, res));
          return attemptAdd(attempt + 1);
        }

        const cartError = await res.json().catch(() => ({}));
        console.error(
          `[Intercars Integration] Add to cart failed (HTTP ${res.status}, attempt ${attempt + 1}):`,
          cartError,
        );
        const err = new Error(`cart/add.js HTTP ${res.status}`);
        err.cartStatus = res.status;
        err.cartError = cartError;
        err.retryable = retryable;
        throw err;
      });
    }

    return attemptAdd(0);
  }

  function startIntercars() {
    const API_BASE_URL =
      window.IntercarsIntegrationConfig?.apiBaseUrl ||
      "https://intercar.api.menvaraosat.fi";

    function initIntercarsForElement(targetElement) {
      if (targetElement.dataset.intercarsInitialized === "true") return;
      // Ensure this is an intercars product by checking the data attribute or global config
      const isIntercars =
        targetElement.dataset.intercars === "true" ||
        (window.IntercarsIntegrationConfig &&
          targetElement.dataset.productId ==
            window.IntercarsIntegrationConfig.productId);

      if (!isIntercars) {
        return; // Not an intercars product, do nothing
      }

      targetElement.dataset.intercarsInitialized = "true";

      let config = window.IntercarsIntegrationConfig;

      let productId =
        targetElement.dataset.productId ||
        targetElement.querySelector('input[name="product-id"]')?.value;
      if (!productId && config) productId = config.productId;

      if (!productId) {
        console.warn(
          "[Intercars Integration] Missing productId. Aborting for this form.",
        );
        return;
      }

      // Extract numeric ID if it already contains gid://
      if (typeof productId === 'string' && productId.includes('/')) {
        productId = productId.split('/').pop();
      }

      const productGid = `gid://shopify/Product/${productId}`;
      let submitBtn = null;

      if (targetElement.tagName === 'FORM') {
          submitBtn = targetElement.querySelector('[name="add"]');
      } else {
          const card = targetElement.closest('.m-product-card') || targetElement.closest('.m-search-item') || targetElement;
          if (card) {
              // A selector list returns the first match in document order, and the
              // quick view icon in the card's top action bar comes before the
              // quick-add button. Picking it left the real Add to cart button in
              // its Liquid `disabled` state whenever the product has no available
              // Shopify variant yet, so look the candidates up by priority.
              submitBtn =
                card.querySelector('.oz-add-cart-btn') ||
                card.querySelector('.m-product-card__quick-add [name="add"], .m-product-card__quick-add .m-add-to-cart') ||
                card.querySelector('.m-add-to-cart, [name="add"]') ||
                card.querySelector('.m-product-card__quick-add .m-product-quickview-button') ||
                card.querySelector('.m-product-quickview-button');
          }
      }

      const submitBtnText = submitBtn
        ? submitBtn.querySelector(".m-add-to-cart--text") || submitBtn
        : null;

      /**
       * The label span is the only node safe to write text into — when the
       * button has none, `submitBtnText` falls back to the button itself and
       * assigning textContent would wipe out its spinner markup.
       */
      const submitBtnLabel = submitBtn
        ? submitBtn.querySelector(".m-add-to-cart--text")
        : null;

      /**
       * Liquid renders the button for the *Shopify* variant. An Intercars
       * product has no purchasable variant until one is created on demand, so
       * `selected_or_first_available_variant.available` is false and the button
       * ships with the `disabled` class (pointer-events: none, see
       * assets/base.css) and the sold-out label. Real stock lives in the
       * Intercars location payload, so from here on the button state is ours.
       */
      const renderedAsUnavailable = submitBtn
        ? submitBtn.classList.contains("disabled") || submitBtn.disabled
        : false;

      /** Theme spinner state — see `.m-spinner-loading` in assets/modules.css. */
      function setButtonSpinner(on) {
        if (!submitBtn) return;
        submitBtn.classList.toggle("m-spinner-loading", on);
      }

      function setButtonAvailable(available, label) {
        if (!submitBtn) return;
        submitBtn.disabled = !available;
        submitBtn.classList.toggle("disabled", !available);
        if (submitBtnLabel && label) submitBtnLabel.textContent = label;
      }

      // Find existing container or create a new one for this form
      let container = targetElement.querySelector(
        ".intercars-locations-container",
      );
      if (!container) {
        // Check if there is an old global ID container and we are the main form
        const oldGlobalContainer = document.getElementById(
          "intercars-locations-container",
        );
        if (
          oldGlobalContainer &&
          !targetElement.closest(".m-product-quickview") &&
          targetElement.tagName === 'FORM'
        ) {
          container = oldGlobalContainer;
          container.classList.add("intercars-locations-container");
          container.removeAttribute("id"); // Remove ID so it doesn't conflict
        } else {
          container = document.createElement("div");
          container.className = "intercars-locations-container m:w-full";

          if (targetElement.tagName === 'FORM') {
            const variantPicker =
              targetElement
                .closest(".m-main-product__wrapper")
                ?.querySelector(".main-product__block-variant_picker") ||
              targetElement
                .closest(".m-product-quickview")
                ?.querySelector(".main-product__block-variant_picker");

            if (variantPicker) {
              variantPicker.appendChild(container);
            } else if (submitBtn && submitBtn.parentNode) {
              submitBtn.parentNode.insertBefore(container, submitBtn);
            } else {
              targetElement.appendChild(container);
            }
          } else {
             // For product cards, append directly to targetElement (.m-product-card__info)
             targetElement.appendChild(container);
          }
        }
      }

      // Store original button text in case we need to revert
      const originalSubmitBtnText = submitBtnText ? submitBtnText.textContent : "";

      const strings = window.MinimogStrings || {};
      const addToCartLabel =
        (submitBtn && submitBtn.dataset.atcText) ||
        strings.addToCart ||
        originalSubmitBtnText.trim();
      const soldOutLabel = strings.soldOut || originalSubmitBtnText.trim();

      /** Back to exactly what Liquid rendered — used on every fallback path. */
      function restoreRenderedButtonState() {
        setButtonSpinner(false);
        setButtonAvailable(
          !renderedAsUnavailable,
          renderedAsUnavailable ? soldOutLabel : addToCartLabel,
        );
        setSectionUnavailable(renderedAsUnavailable);
      }

      /**
       * sections/main-product.liquid marks the whole section `unavailable`
       * when the Shopify product has no available variant, and
       * assets/product.css then greys out the form and sets
       * `pointer-events: none` on it — the button looks and acts dead even
       * though it is enabled. For Intercars products Liquid leaves the class
       * off and we drive it from the Intercars stock instead. The sold-out
       * price styling follows the same rule.
       */
      function setSectionUnavailable(unavailable) {
        if (targetElement.tagName !== "FORM") return;
        const section = targetElement.closest(".m-section__wrapper");
        if (!section || !section.querySelector(".m-main-product__wrapper")) {
          return;
        }
        section.classList.toggle("unavailable", unavailable);
        const price = section.querySelector(
          ".main-product__block-price .m-price",
        );
        if (price) price.classList.toggle("m-price--sold-out", unavailable);
      }

      /**
       * Intercars products usually have no Shopify description, so the text
       * from the backend goes into the slot each context already has: the
       * description section on the product page and the description block in
       * quick view. Product cards hide `.m-product-card__description` outside
       * list view, so they get their own line in the text column under the
       * price — kept outside `container`, whose innerHTML the picker rewrites.
       * Layout lives in assets/component-product-card.css.
       */
      function renderDescription(description) {
        const text = typeof description === "string" ? description.trim() : "";
        if (!text) return;

        if (targetElement.tagName !== "FORM") {
          let descEl = targetElement.querySelector(".intercars-description");
          if (!descEl) {
            descEl = document.createElement("div");
            descEl.className = "intercars-description";
            const textColumn = targetElement.querySelector(
              ".m-product-card__info-wrapper",
            );
            if (textColumn) {
              const price = textColumn.querySelector(".m-product-card__price");
              textColumn.insertBefore(
                descEl,
                price ? price.nextSibling : null,
              );
            } else {
              container.parentNode.insertBefore(descEl, container);
            }
          }
          descEl.textContent = text;
          descEl.title = text;
          return;
        }

        const quickview = targetElement.closest(".m-product-quickview");
        if (quickview) {
          const descText = quickview.querySelector(
            ".m-product-quickview__description-text",
          );
          if (descText) descText.textContent = text;
          return;
        }

        const section = document.querySelector(
          ".product-description-section .container",
        );
        if (!section) return;
        let descEl = section.querySelector(".product-description-container");
        if (!descEl) {
          descEl = document.createElement("div");
          descEl.className = "product-description-container rte";
          section.appendChild(descEl);
        }
        descEl.textContent = text;
      }

      const isCompactModeSkeleton = targetElement.tagName !== 'FORM';
      let skeletonHtml = '';

      if (isCompactModeSkeleton) {
        skeletonHtml = `
          <div class="intercars-picker-wrapper intercars-compact m:w-full">
              <div style="font-size: 13px; font-weight: 500; margin-bottom: 6px; color: var(--color-text-main);">Toimitusaika:</div>
              <div class="intercars-compact-radio-list m:flex m:flex-col" style="gap: 6px; font-size: 12px; color: var(--color-text-main);">
                  <div class="intercars-compact-option m:flex m:items-center" style="opacity: 0.5;">
                      <svg class="animate-spin m-svg-icon--small" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" style="margin-right: 6px; width: 14px; height: 14px;">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span style="margin: 0; line-height: 1.4; color: var(--color-text-main);">Ladataan...</span>
                  </div>
              </div>
          </div>
        `;
      } else {
        skeletonHtml = `
          <div class="intercars-picker-wrapper">
              <div class="m-product-option m-product-option--button">
                  <div class="intercars-variant-button" data-picker-field="radio" data-option-name="Toimitusaika">
                      <div class="m-product-option--label">
                          <label class="option-label">
                              <span class="option-label--title">Toimitusaika:</span>
                              <span class="option-label--selected intercars-selected-location">
                                  <svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" style="display:inline-block; vertical-align:middle; width: 14px; height: 14px; margin-right: 4px;">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg> Ladataan...
                              </span>
                          </label>
                      </div>
                      <div class="m-product-option--content m:inline-flex m:flex-wrap">
                          <div class="m-product-option--node" style="opacity: 0.5; pointer-events: none;">
                              <label class="m-product-option--node__label">Ladataan...</label>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
        `;
      }

      container.innerHTML = skeletonHtml;

      // Hold the button until we know the Intercars stock. Showing the spinner
      // rather than the Liquid "sold out" label matters: the product is very
      // likely buyable, we just don't know from which location yet.
      if (submitBtn) {
        setButtonAvailable(false);
        setButtonSpinner(true);
      }

      let selectedLocationId = null;
      const fetchUrl = `${API_BASE_URL}/api/intercars/product-variants/${encodeURIComponent(productGid)}`;

      fetch(fetchUrl)
        .then(async (res) => {
          if (!res.ok) {
            const errText = await res.text();
            throw new Error(`HTTP ${res.status}: ${errText}`);
          }
          return res.json();
        })
        .then((data) => {
          renderDescription(data && data.description);

          const locations = Array.isArray(data.shopifyInventoryJson)
            ? data.shopifyInventoryJson.filter(
                (loc) => !HIDDEN_LOCATIONS.has(locationKey(loc)),
              )
            : null;

          if (!Array.isArray(locations) || locations.length === 0) {
            console.warn(
              "[Intercars Integration] shopifyInventoryJson is empty or not an array. Falling back to default.",
            );
            container.innerHTML = '';
            restoreRenderedButtonState();
            targetElement.dataset.intercars = "false"; // Prevent further intercars handling
            return;
          }

          renderLocationPicker(locations);

          // Stock is per Intercars location, so the button follows the picker:
          // buyable as soon as any location can ship, sold out only when none can.
          const hasStock = locations.some((loc) => Number(loc.available) > 0);
          setButtonSpinner(false);
          setButtonAvailable(
            hasStock,
            hasStock ? addToCartLabel : soldOutLabel,
          );
          setSectionUnavailable(!hasStock);
        })
        .catch((err) => {
          console.warn(
            "[Intercars Integration] Error fetching intercars variants (likely not an Intercars product):",
            err,
          );
          // Gracefully fallback. Never force-enable here — without the Intercars
          // answer the only trustworthy state is the one Liquid rendered.
          container.innerHTML = '';
          restoreRenderedButtonState();
          targetElement.dataset.intercars = "false"; // Prevent further intercars handling
        });

      function renderLocationPicker(locations) {
        const firstAvailableIndex = locations.findIndex(
          (loc) => loc.available > 0,
        );
        const isCompactMode = targetElement.tagName !== 'FORM';

        let html = '';

        if (isCompactMode) {
          html = `
            <div class="intercars-picker-wrapper intercars-compact m:w-full">
                <div style="font-size: 13px; font-weight: 500; margin-bottom: 6px; color: var(--color-text-main);">Toimitusaika:</div>
                <div class="intercars-compact-radio-list m:flex m:flex-col" style="gap: 6px; font-size: 12px; color: var(--color-text-main);">
          `;

          locations.forEach((loc, index) => {
            const isAvailable = loc.available > 0;
            const isChecked = index === firstAvailableIndex;
            if (isChecked) selectedLocationId = loc.shopifyLocationId;

            const labelText = formatDeliveryText(loc);

            html += `
                <div class="intercars-compact-option m:flex m:items-center" style="${!isAvailable ? 'opacity: 0.5;' : ''}">
                    <span class="intercars-compact-option-dot" style="margin-right: 6px; background-color: ${isAvailable ? '#10b981' : '#ef4444'}; font-size: 14px; line-height: 1.4;"></span>
                    <span style="margin: 0; line-height: 1.4; color: var(--color-text-main);">
                        ${labelText}
                    </span>
                </div>
            `;
          });

          html += `
                </div>
            </div>
          `;
        } else {
          html = `
                  <div class="intercars-picker-wrapper">
                      <div class="m-product-option m-product-option--button">
                          <div class="intercars-variant-button" data-picker-field="radio" data-option-name="Toimitusaika">
                              <div class="m-product-option--label">
                                  <label class="option-label">
                                      <span class="option-label--title">Toimitusaika:</span>
                                      <span class="option-label--selected intercars-selected-location">
                                          ${firstAvailableIndex >= 0 ? formatDeliveryText(locations[firstAvailableIndex]) : "Select option"}
                                      </span>
                                  </label>
                              </div>
                              <div class="m-product-option--content m:inline-flex m:flex-wrap">
              `;

          locations.forEach((loc, index) => {
            const isAvailable = loc.available > 0;
            const isChecked = index === firstAvailableIndex;
            if (isChecked) selectedLocationId = loc.shopifyLocationId;

            const labelText = formatDeliveryText(loc);

            html += `
                      <div class="m-product-option--node ${!isAvailable ? "m-product-option--node__soldout" : ""}" data-option-position="1" data-option-type="button" data-value="${labelText}">
                          <input type="radio" 
                                 id="intercars-loc-${productId}-${index}" 
                                 name="intercars_location_${productId}" 
                                 value="${labelText}" 
                                 data-location-id="${loc.shopifyLocationId}"
                                 data-available="${loc.available}"
                                 data-variant-id="${loc.shopifyProductVariantId || ""}"
                                 class="intercars-loc-radio"
                                 ${isChecked ? "checked" : ""}
                                 ${!isAvailable ? "disabled" : ""}>
                          <label for="intercars-loc-${productId}-${index}" class="m-product-option--node__label">
                              ${labelText}
                              ${
                                isAvailable
                                  ? `<span class="intercars-stock">${INTERCARS_STRINGS.inStock(Number(loc.available), 0)}</span>`
                                  : // Empty line keeps sold-out buttons as tall as the others.
                                    `<span class="intercars-stock intercars-stock--empty" aria-hidden="true">&nbsp;</span>`
                              }
                          </label>
                      </div>
                  `;
          });

          html += `
                              </div>
                          </div>
                      </div>
                      <div class="intercars-split-note" hidden></div>
                  </div>
              `;
        }

        container.innerHTML = html;

        if (!isCompactMode) {
          const radios = container.querySelectorAll(".intercars-loc-radio");
          radios.forEach((radio) => {
            radio.addEventListener("change", (e) => {
              selectedLocationId = e.target.dataset.locationId;
              const selLocationSpan = container.querySelector(
                ".intercars-selected-location",
              );
              // The radio value is the plain label, without the stock line.
              if (selLocationSpan) selLocationSpan.textContent = e.target.value;
              syncQuantityLimit();
              renderSplitNote();
              // The cart may have changed elsewhere (drawer, another tab).
              refreshCartQuantities();
              prewarmVariant(selectedLocationId);
            });
          });
        }

        /** Numeric variant id for a location, if it exists or was created on this page. */
        function knownVariantIdFor(location) {
          return knownVariantId(productGid, location);
        }

        /** variantId -> quantity already in the cart. Empty on failure. */
        function fetchCartQuantities() {
          return fetch(window.Shopify.routes.root + "cart.js", {
            headers: { Accept: "application/json" },
          })
            .then((res) => (res.ok ? res.json() : { items: [] }))
            .then((cart) =>
              (cart.items || []).reduce((acc, item) => {
                const id = String(item.variant_id);
                acc[id] = (acc[id] || 0) + Number(item.quantity || 0);
                return acc;
              }, {}),
            )
            .catch(() => ({}));
        }

        // Last known cart contents (variantId -> quantity), refreshed on render
        // and on every Add to cart. Units already in the cart are not free.
        let cartQuantities = {};

        function getFreeStock() {
          let inCartTotal = 0;
          const freeLocations = locations.map((loc) => {
            const available = Math.max(0, Number(loc.available) || 0);
            const variantId = knownVariantIdFor(loc);
            const inCart = variantId ? cartQuantities[variantId] || 0 : 0;
            inCartTotal += Math.min(inCart, available);
            return { ...loc, available: Math.max(0, available - inCart) };
          });
          return {
            freeLocations,
            freeTotal: getTotalIntercarsStock(freeLocations),
            inCartTotal,
          };
        }

        function refreshCartQuantities() {
          return fetchCartQuantities().then((quantities) => {
            cartQuantities = quantities;
            syncQuantityLimit();
            renderStockLabels();
            renderSplitNote();
          });
        }

        /**
         * Shows next to each location's stock how much of it is already in the
         * cart — otherwise it is unclear why picking a location whose stock is
         * all in the cart still ships the order from another one.
         */
        function renderStockLabels() {
          if (isCompactMode) return;
          container.querySelectorAll(".intercars-loc-radio").forEach((radio) => {
            const loc = locations.find(
              (l) => String(l.shopifyLocationId) === String(radio.dataset.locationId),
            );
            const stockEl = container.querySelector(
              `label[for="${radio.id}"] .intercars-stock`,
            );
            if (!loc || !stockEl) return;
            const available = Math.max(0, Number(loc.available) || 0);
            if (available === 0) return;
            const variantId = knownVariantIdFor(loc);
            const inCart = Math.min(
              available,
              variantId ? cartQuantities[variantId] || 0 : 0,
            );
            stockEl.textContent = INTERCARS_STRINGS.inStock(available, inCart);
          });
        }

        function getQtyInput() {
          // getAttribute: the form has an `<input name="id">`, which shadows `form.id`.
          const formId = targetElement.getAttribute("id");
          return (
            (formId &&
              document.querySelector(
                `input[name="quantity"][form="${CSS.escape(formId)}"]`,
              )) ||
            targetElement.querySelector('input[name="quantity"]')
          );
        }

        function getRequestedQuantity() {
          if (isCompactMode) return 1;
          const qtyInput = getQtyInput();
          const value = qtyInput ? parseInt(qtyInput.value, 10) : 1;
          return value >= 1 ? value : 1;
        }

        /**
         * Liquid caps the quantity input at the Shopify variant's
         * `inventory_quantity`. Before our variant exists that is the sold-out
         * placeholder, so the input ships with max="0" and both +/- buttons
         * disabled. The real limit is the combined free stock of all Intercars
         * locations — anything above the selected location's stock is split
         * across the others on Add to cart.
         */
        function syncQuantityLimit() {
          if (isCompactMode) return;
          const qtyInput = getQtyInput();
          if (!qtyInput) return;

          // Never 0: with everything already in the cart the click explains why.
          const limit = Math.max(1, getFreeStock().freeTotal);
          qtyInput.max = String(limit);
          qtyInput.dataset.max = String(limit);
          clampQuantity(qtyInput);
          // m-quantity-input re-validates its +/- buttons on change.
          qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
        }

        /** m-quantity-input does not clamp typed values, so keep them in range. */
        function clampQuantity(qtyInput) {
          const current = parseInt(qtyInput.value, 10);
          let next = current;
          if (!(current >= 1)) next = 1;
          else {
            const limit = Math.max(1, getFreeStock().freeTotal);
            if (current > limit) next = limit;
          }
          if (next === current) return false;
          qtyInput.value = String(next);
          return true;
        }

        /** Tells the customer up front how an order above one location's stock ships. */
        function renderSplitNote() {
          const note = container.querySelector(".intercars-split-note");
          if (!note) return;

          const { allocations } = allocateIntercarsQuantity(
            getFreeStock().freeLocations,
            selectedLocationId,
            getRequestedQuantity(),
          );
          // Also shown when the selected location has nothing free (all of it
          // is already in the cart) and the whole order ships from another one.
          const shipsAsSelected =
            allocations.length < 2 &&
            (allocations.length === 0 ||
              String(allocations[0].location.shopifyLocationId) ===
                String(selectedLocationId));
          if (shipsAsSelected) {
            note.hidden = true;
            note.innerHTML = "";
            return;
          }

          note.innerHTML = `
            <p class="intercars-split-note__title">${
              allocations.length > 1
                ? INTERCARS_STRINGS.splitNoteTitle
                : INTERCARS_STRINGS.otherLocationNoteTitle
            }</p>
            <ul class="intercars-split-note__list">
              ${allocations
                .map(
                  (a) =>
                    `<li>${INTERCARS_STRINGS.splitNoteLine(a.quantity, formatDeliveryText(a.location))}</li>`,
                )
                .join("")}
            </ul>
          `;
          note.hidden = false;
        }

        if (!isCompactMode) {
          const qtyInput = getQtyInput();
          if (qtyInput) {
            const onQuantityChange = (e) => {
              if (e.type === "change" && clampQuantity(qtyInput)) {
                qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
                return;
              }
              renderSplitNote();
            };
            qtyInput.addEventListener("input", onQuantityChange);
            qtyInput.addEventListener("change", onQuantityChange);
            // The cart may have changed in the drawer since the last refresh.
            qtyInput.addEventListener("focus", refreshCartQuantities);
          }
        }

        syncQuantityLimit();
        renderSplitNote();
        if (!isCompactMode) {
          refreshCartQuantities();
          // Cart drawer quantity changes and removals emit the theme's cartUpdate.
          if (
            window.MinimogEvents &&
            window.MinimogTheme &&
            window.MinimogTheme.pubSubEvents
          ) {
            // Quick view renders a new form on every open; drop the listener of
            // one that has left the page instead of refreshing it forever.
            const unsubscribe = window.MinimogEvents.subscribe(
              window.MinimogTheme.pubSubEvents.cartUpdate,
              () => {
                if (!targetElement.isConnected) {
                  if (typeof unsubscribe === "function") unsubscribe();
                  return;
                }
                refreshCartQuantities();
              },
            );
          }
        }

        /**
         * Creating the variant on click costs the customer the Admin API write
         * plus the storefront propagation wait. Locations without a variant are
         * known at render time (empty shopifyProductVariantId), so start the
         * creation as soon as the picker is shown, and again when the selection
         * changes. By the time Add to cart is clicked the variant is usually
         * ready and the fast path applies.
         *
         * Deliberately skipped in compact mode: a product listing renders dozens
         * of cards at once and pre-warming all of them would flood the Admin API.
         */
        function prewarmVariant(locationId) {
          if (isCompactMode || !locationId) return;

          const loc = locations.find(
            (l) => String(l.shopifyLocationId) === String(locationId),
          );
          if (!loc || loc.shopifyProductVariantId) return;

          createIntercarsVariant(API_BASE_URL, productGid, locationId).catch(
            (err) => {
              console.warn(
                "[Intercars Integration] Variant pre-warm failed; will retry on Add to cart:",
                err,
              );
            },
          );
        }

        prewarmVariant(selectedLocationId);


        async function handleAddToCart(e) {
          if (targetElement.dataset.intercarsProcessed === "true") {
            return;
          }

          // If it's a quick view button (not the actual Add to cart button inside the quick view form), we don't hijack it.
          if (
            submitBtn &&
            submitBtn.classList.contains("m-product-quickview-button") &&
            targetElement.tagName !== "FORM"
          ) {
            return;
          }

          // We always add to the cart ourselves: the allocation depends on what
          // is already in the cart, which needs an await, and the theme's native
          // handler can't be cancelled after one.
          e.preventDefault();
          e.stopImmediatePropagation();

          if (!selectedLocationId) {
            IntercarsOverlay.error({
              title: INTERCARS_STRINGS.noLocationTitle,
              reload: false,
            });
            return;
          }

          targetElement.dataset.intercarsProcessed = "true";
          // Keep the label as-is — only the spinner and the lock change here.
          setButtonAvailable(false);
          setButtonSpinner(true);

          // Intercars stock is per location, but units of a location's variant
          // already in the cart are no longer free to add.
          cartQuantities = await fetchCartQuantities();
          const { freeLocations, freeTotal, inCartTotal } = getFreeStock();

          // More than the selected location has free is taken from the other
          // locations in turn, as separate cart lines.
          const { allocations, remaining } = allocateIntercarsQuantity(
            freeLocations,
            selectedLocationId,
            getRequestedQuantity(),
          );

          if (remaining > 0 || allocations.length === 0) {
            failAddToCart({
              title: INTERCARS_STRINGS.notEnoughStockTitle,
              text: INTERCARS_STRINGS.notEnoughStockText(freeTotal, inCartTotal),
              reload: false,
            });
            return;
          }

          // Only variant creation (plus the cart retries after it) is slow
          // enough to need the blocking overlay; the button spinner covers the rest.
          if (allocations.some((a) => !knownVariantIdFor(a.location))) {
            IntercarsOverlay.show();
          }

          function failAddToCart(options) {
            const opts = options || {};
            setButtonSpinner(false);
            setButtonAvailable(true, addToCartLabel);
            targetElement.dataset.intercarsProcessed = "false";
            IntercarsOverlay.error({
              title: opts.title || INTERCARS_STRINGS.errorTitle,
              text: opts.text || INTERCARS_STRINGS.errorText,
              reload: opts.reload !== false,
            });
          }

          // Set when an allocation needs a variant created on this click — only
          // then is a 422 from cart/add.js inventory lag worth waiting out.
          let hasFreshVariant = false;

          /**
           * One allocation goes through the form (keeps any extra form fields).
           * A split order is sent as one `items` request, the way the theme adds
           * product bundles — Shopify rejects the whole request if any line
           * fails, so a retry never duplicates lines that already went in.
           */
          function buildCartAddRequest(items, cartDrawer) {
            const sections =
              cartDrawer && typeof cartDrawer.getSectionsToRender === "function"
                ? cartDrawer.getSectionsToRender().map((el) => el.id)
                : null;

            if (items.length > 1) {
              // Line item properties (e.g. `properties[_sufio]` used for
              // invoices) go on every line, as the form would send them — without
              // them the lines also don't merge with ones added via the form.
              const properties = {};
              if (targetElement.tagName === "FORM") {
                new FormData(targetElement).forEach((value, key) => {
                  const match = /^properties\[(.+)\]$/.exec(key);
                  if (match && typeof value === "string") properties[match[1]] = value;
                });
              }
              const hasProperties = Object.keys(properties).length > 0;
              const body = {
                items: items.map((item) =>
                  hasProperties ? { ...item, properties } : item,
                ),
              };
              if (sections) {
                body.sections = sections;
                body.sections_url = window.location.pathname;
              }
              return {
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                  "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify(body),
              };
            }

            const formData =
              targetElement.tagName === "FORM"
                ? new FormData(targetElement)
                : new FormData();
            formData.set("id", items[0].id);
            formData.set("quantity", String(items[0].quantity));
            if (sections) {
              formData.append("sections", sections.join(","));
              formData.append("sections_url", window.location.pathname);
            }
            return {
              headers: { "X-Requested-With": "XMLHttpRequest" },
              body: formData,
            };
          }

          function submitToCart(items) {
            const cartDrawer = document.querySelector("m-cart-drawer");

            addToCartWithRetry(
              () => buildCartAddRequest(items, cartDrawer),
              hasFreshVariant,
            )
              .then((data) => {
                if (cartDrawer) {
                  // Hand the screen back before the drawer opens. When there's
                  // no drawer we keep the overlay up through the /cart redirect.
                  IntercarsOverlay.hide();

                  if (typeof cartDrawer.renderContents === "function") {
                    cartDrawer.renderContents(data);
                  }
                  if (
                    window.MinimogTheme &&
                    window.MinimogTheme.Notification
                  ) {
                    window.MinimogTheme.Notification.show({
                      target:
                        cartDrawer.querySelector("m-cart-drawer-items") ||
                        document.body,
                      method: "prepend",
                      type: "success",
                      message: window.MinimogStrings
                        ? window.MinimogStrings.itemAdded
                        : "Added to cart",
                      delay: 400,
                    });
                  }
                  if (
                    window.MinimogEvents &&
                    window.MinimogTheme &&
                    window.MinimogTheme.pubSubEvents
                  ) {
                    window.MinimogEvents.emit(
                      window.MinimogTheme.pubSubEvents.openCartDrawer,
                    );
                  }
                } else {
                  window.location.href = window.Shopify.routes.root + "cart";
                }

                // Same event the theme's own product form emits — the header
                // cart count bubble (assets/cart.js) and quick view listen to it.
                if (
                  window.MinimogEvents &&
                  window.MinimogTheme &&
                  window.MinimogTheme.pubSubEvents
                ) {
                  window.MinimogEvents.emit(
                    window.MinimogTheme.pubSubEvents.cartUpdate,
                    data,
                  );
                }

                setButtonSpinner(false);
                setButtonAvailable(true, addToCartLabel);
                // The limit and the split note now have less free stock.
                refreshCartQuantities();
                setTimeout(() => {
                  targetElement.dataset.intercarsProcessed = "false";
                }, 500);

                const quickview = targetElement.closest(
                  ".m-product-quickview",
                );
                if (quickview) {
                  const closeBtn = quickview.querySelector(
                    "[data-close-quickview]",
                  );
                  if (closeBtn) closeBtn.click();
                }
              })
              .catch((error) => {
                if (error && error.cartStatus) {
                  failAddToCart({
                    // Shopify localises `description` to the store locale (fi),
                    // so it is safe to show verbatim when present.
                    text:
                      error.cartError.description || INTERCARS_STRINGS.errorText,
                    // Reloading only helps when we ran out of propagation time;
                    // for a sold-out answer it changes nothing.
                    reload: error.retryable,
                  });
                  return;
                }
                console.error("Error adding to cart:", error);
                failAddToCart({});
              });
          }

          // Variants that already exist are used as-is; the rest are created.
          // createIntercarsVariant reuses the pre-warm promise when one is in
          // flight or already resolved, so the click never triggers a second
          // Admin API write.
          Promise.all(
            allocations.map((a) => {
              const knownId =
                a.location.shopifyProductVariantId ||
                getSettledVariantId(productGid, a.location.shopifyLocationId);
              if (!knownId) hasFreshVariant = true;
              return (
                knownId
                  ? Promise.resolve(knownId)
                  : createIntercarsVariant(
                      API_BASE_URL,
                      productGid,
                      a.location.shopifyLocationId,
                    )
              ).then((variantId) => ({
                // Strip gid:// — cart/add.js wants the numeric id.
                id: String(variantId).split("/").pop(),
                quantity: a.quantity,
              }));
            }),
          )
            .then((items) => {
              submitToCart(items);
            })
            .catch((error) => {
              console.error("Error calling update-or-create-variant:", error);
              if (error && error.intercarsNoVariant) {
                failAddToCart({
                  title: INTERCARS_STRINGS.noVariantTitle,
                  text: INTERCARS_STRINGS.noVariantText,
                });
              } else {
                failAddToCart({});
              }
            });
        }

        if (submitBtn) {
          submitBtn.addEventListener("click", handleAddToCart, true);
        }
      }
    }
    /**
     * Cart drawer quantity for Intercars lines follows the product page rules.
     * Each line is one location's variant, so the input's limit is what that
     * location still has free plus everything free at the other locations.
     * Raising a line past its own location's stock fills it up to that stock
     * and adds the rest from the other locations (fastest delivery first) as
     * their own lines, creating variants where needed.
     */
    const DRAWER_LINE_SELECTOR =
      '.m-cart-drawer-item[data-intercars="true"] input[data-line-key]';
    const DRAWER_STOCK_TTL_MS = 60000;
    const DRAWER_CHANGE_DEBOUNCE_MS = 300; // same as the theme's own handler
    const drawerStock = new Map(); // productId -> { promise, fetchedAt }
    const drawerTimers = new WeakMap(); // input -> debounce timer

    const JSON_HEADERS = {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    };

    function productGidFor(productId) {
      return `gid://shopify/Product/${productId}`;
    }

    /** Visible Intercars locations of a product, cached briefly across re-renders. */
    function fetchProductLocations(productId) {
      const cached = drawerStock.get(productId);
      if (cached && Date.now() - cached.fetchedAt < DRAWER_STOCK_TTL_MS) {
        return cached.promise;
      }
      const promise = fetch(
        `${API_BASE_URL}/api/intercars/product-variants/${encodeURIComponent(productGidFor(productId))}`,
      )
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((data) =>
          (Array.isArray(data && data.shopifyInventoryJson)
            ? data.shopifyInventoryJson
            : []
          ).filter((loc) => !HIDDEN_LOCATIONS.has(locationKey(loc))),
        )
        .catch((err) => {
          drawerStock.delete(productId);
          throw err;
        });
      drawerStock.set(productId, { promise, fetchedAt: Date.now() });
      return promise;
    }

    function fetchCart() {
      return fetch(window.Shopify.routes.root + "cart.js", {
        headers: { Accept: "application/json" },
      }).then((res) => {
        if (!res.ok) throw new Error(`cart.js HTTP ${res.status}`);
        return res.json();
      });
    }

    /**
     * Stock picture for one cart line. `allowedHere` is how many the line can
     * hold from its own location, `others` the free stock elsewhere and `max`
     * the most the line can be raised to. Null when the line's variant can't
     * be matched to a location — the theme's own handling applies then.
     */
    function getDrawerLineStock(locations, productGid, cart, lineKey) {
      const items = (cart && cart.items) || [];
      const line = items.find((item) => item.key === lineKey);
      if (!line) return null;

      const inCart = {};
      items.forEach((item) => {
        const id = String(item.variant_id);
        inCart[id] = (inCart[id] || 0) + Number(item.quantity || 0);
      });

      let own = null;
      const others = [];
      locations.forEach((loc) => {
        const variantId = knownVariantId(productGid, loc);
        const available = Math.max(0, Number(loc.available) || 0);
        const free = {
          ...loc,
          available: Math.max(0, available - (variantId ? inCart[variantId] || 0 : 0)),
        };
        if (variantId === String(line.variant_id)) own = free;
        else others.push(free);
      });
      if (!own) return null;

      const allowedHere = Number(line.quantity) + own.available;
      return {
        line,
        others,
        allowedHere,
        max: allowedHere + getTotalIntercarsStock(others),
      };
    }

    function setDrawerInputMax(input, max) {
      const limit = String(Math.max(1, max));
      input.max = limit;
      input.dataset.max = limit;
      const quantityEl = input.closest("m-quantity-input");
      if (quantityEl && typeof quantityEl.validateQtyRules === "function") {
        quantityEl.validateQtyRules();
      }
    }

    function notifyDrawer(target, type, message) {
      if (!target || !window.MinimogTheme || !window.MinimogTheme.Notification) {
        return;
      }
      window.MinimogTheme.Notification.show({
        target,
        method: "prepend",
        type,
        message,
      });
    }

    function emitCartUpdate(data) {
      if (
        window.MinimogEvents &&
        window.MinimogTheme &&
        window.MinimogTheme.pubSubEvents
      ) {
        // Same payload the theme's drawer emits after its own quantity change.
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
          ...data,
          source: "main-cart-items",
        });
      }
    }

    /**
     * Sets the +/- limits of the drawer's Intercars lines. Deferred until the
     * drawer is open so page loads don't pay for cart.js and the Intercars
     * API when nobody looks at the drawer.
     */
    function initDrawerQuantities() {
      if (!document.querySelector("m-cart-drawer.m-cart-drawer--active")) return;
      const inputs = Array.from(
        document.querySelectorAll(DRAWER_LINE_SELECTOR),
      ).filter((input) => input.dataset.intercarsInitialized !== "true");
      if (inputs.length === 0) return;
      inputs.forEach((input) => {
        input.dataset.intercarsInitialized = "true";
      });

      fetchCart()
        .then((cart) => {
          inputs.forEach((input) => {
            const item = input.closest(".m-cart-drawer-item");
            const productId = item && item.dataset.productId;
            if (!productId) return;
            fetchProductLocations(productId)
              .then((locations) => {
                if (!input.isConnected) return;
                const stock = getDrawerLineStock(
                  locations,
                  productGidFor(productId),
                  cart,
                  input.dataset.lineKey,
                );
                if (stock) setDrawerInputMax(input, stock.max);
              })
              .catch((err) => {
                console.warn(
                  "[Intercars Integration] Could not load drawer stock:",
                  err,
                );
              });
          });
        })
        .catch((err) => {
          console.warn("[Intercars Integration] Could not load cart:", err);
        });
    }

    function renderDrawerSections(drawerItems, data) {
      drawerItems.getSectionsToRender().forEach((section) => {
        const html = data.sections && data.sections[section.section];
        const host = document.getElementById(section.id);
        if (!html || !host) return;
        (host.querySelector(section.selector) || host).innerHTML =
          drawerItems.getSectionInnerHTML(html, section.selector);
      });
    }

    async function updateDrawerLine(input) {
      if (!input.isConnected) return;
      const drawerItems = input.closest("m-cart-drawer-items");
      const item = input.closest(".m-cart-drawer-item");
      if (!drawerItems || !item) return;

      const index = input.dataset.index;
      const productId = item.dataset.productId;
      const productGid = productGidFor(productId);
      const requested = parseInt(input.value, 10);
      const updateNatively = (quantity) =>
        drawerItems.updateQuantity(index, quantity);

      // Removing and lowering never touch other locations.
      if (!(requested > 0)) {
        updateNatively(input.value);
        return;
      }

      drawerItems.loading.start();
      let stock = null;
      try {
        const [cart, locations] = await Promise.all([
          fetchCart(),
          fetchProductLocations(productId),
        ]);
        stock = getDrawerLineStock(
          locations,
          productGid,
          cart,
          input.dataset.lineKey,
        );
      } catch (err) {
        console.warn("[Intercars Integration] Could not load drawer stock:", err);
      }
      drawerItems.loading.finish();

      // Unknown stock: let Shopify cap the line as it would without us.
      if (!stock) {
        updateNatively(requested);
        return;
      }

      const target = Math.min(requested, stock.max);
      if (target < requested) {
        notifyDrawer(drawerItems, "warning", INTERCARS_STRINGS.notEnoughStockTitle);
      }
      if (target <= stock.allowedHere) {
        input.value = String(target);
        updateNatively(target);
        return;
      }

      const { allocations } = allocateIntercarsQuantity(
        stock.others,
        null,
        target - stock.allowedHere,
      );

      drawerItems.loading.start();
      if (allocations.some((a) => !knownVariantId(productGid, a.location))) {
        IntercarsOverlay.show();
      }

      let added = false;
      try {
        let hasFreshVariant = false;
        const variantItems = await Promise.all(
          allocations.map((a) => {
            const knownId =
              a.location.shopifyProductVariantId ||
              getSettledVariantId(productGid, a.location.shopifyLocationId);
            if (!knownId) hasFreshVariant = true;
            return (
              knownId
                ? Promise.resolve(knownId)
                : createIntercarsVariant(
                    API_BASE_URL,
                    productGid,
                    a.location.shopifyLocationId,
                  )
            ).then((variantId) => ({
              id: String(variantId).split("/").pop(),
              quantity: a.quantity,
            }));
          }),
        );

        // Same line item properties as the line being raised (e.g. `_sufio`),
        // so the new lines match ones the product page would have added.
        const properties = stock.line.properties || {};
        const hasProperties = Object.keys(properties).length > 0;
        const addItems = variantItems.map((v) =>
          hasProperties ? { ...v, properties } : v,
        );

        await addToCartWithRetry(
          () => ({
            headers: JSON_HEADERS,
            body: JSON.stringify({ items: addItems }),
          }),
          hasFreshVariant,
        );
        added = true;

        // By key, not line index — the added lines shift the indexes.
        const res = await fetch(window.Shopify.routes.root + "cart/change.js", {
          method: "POST",
          headers: JSON_HEADERS,
          body: JSON.stringify({
            id: stock.line.key,
            quantity: stock.allowedHere,
            sections: drawerItems.getSectionsToRender().map((s) => s.section),
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();
        if (!res.ok || data.errors) {
          throw new Error(`cart/change.js HTTP ${res.status}`);
        }

        IntercarsOverlay.hide();
        renderDrawerSections(drawerItems, data);
        notifyDrawer(
          drawerItems,
          "success",
          `${INTERCARS_STRINGS.drawerSplitTitle} ${allocations
            .map((a) =>
              INTERCARS_STRINGS.splitNoteLine(
                a.quantity,
                formatDeliveryText(a.location),
              ),
            )
            .join(", ")}`,
        );
        emitCartUpdate(data);
      } catch (error) {
        console.error("[Intercars Integration] Drawer quantity update failed:", error);
        input.value = String(stock.line.quantity);
        setDrawerInputMax(input, stock.max);
        if (added) {
          // The extra lines went in; show them even though the line itself
          // could not be raised.
          const cartDrawer = document.querySelector("m-cart-drawer");
          if (cartDrawer && typeof cartDrawer.onCartDrawerUpdate === "function") {
            cartDrawer.onCartDrawerUpdate();
          }
          emitCartUpdate({});
        }
        if (error && error.intercarsNoVariant) {
          IntercarsOverlay.error({
            title: INTERCARS_STRINGS.noVariantTitle,
            text: INTERCARS_STRINGS.noVariantText,
          });
        } else {
          IntercarsOverlay.error({
            text:
              (error && error.cartError && error.cartError.description) ||
              INTERCARS_STRINGS.errorText,
            reload: !error || !error.cartStatus || error.retryable,
          });
        }
      } finally {
        drawerItems.loading.finish();
      }
    }

    // Capture phase: runs before m-quantity-input and the drawer's own
    // debounced change handler, which would send the raw value to cart/change.
    document.addEventListener(
      "change",
      (e) => {
        const input = e.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (!input.matches(DRAWER_LINE_SELECTOR)) return;
        e.stopImmediatePropagation();

        const quantityEl = input.closest("m-quantity-input");
        if (quantityEl && typeof quantityEl.validateQtyRules === "function") {
          quantityEl.validateQtyRules();
        }
        clearTimeout(drawerTimers.get(input));
        drawerTimers.set(
          input,
          setTimeout(() => updateDrawerLine(input), DRAWER_CHANGE_DEBOUNCE_MS),
        );
      },
      true,
    );

    if (
      window.MinimogEvents &&
      window.MinimogTheme &&
      window.MinimogTheme.pubSubEvents
    ) {
      window.MinimogEvents.subscribe(
        window.MinimogTheme.pubSubEvents.openCartDrawer,
        initDrawerQuantities,
      );
    }

    // 1. Scan and initialize existing forms
    function scanAndInit() {
      const forms = document.querySelectorAll(
        'form[data-type="add-to-cart-form"], .m-product-card__info[data-intercars="true"]',
      );
      forms.forEach((form) => {
        initIntercarsForElement(form);
      });
      // The drawer re-renders its lines after every cart change.
      initDrawerQuantities();
    }

    scanAndInit();

    // 2. Observe DOM for dynamically added forms (like quickview)
    const observer = new MutationObserver((mutations) => {
      let shouldScan = false;
      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0) {
          // Check if any added node is an element (ignore text nodes)
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              shouldScan = true;
              break;
            }
          }
        }
        if (shouldScan) break;
      }
      if (shouldScan) {
        scanAndInit();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

  } // End startIntercars

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startIntercars);
  } else {
    startIntercars();
  }
})();
